/**
 * CSV column families for the admin sensor graph, matching the old logs page
 * (logging/app.js SERIES_FAMILIES). Order matters: Filtered_Raw_ before Filtered_.
 */
export interface Family {
  id: string;
  title: string;
  desc: string;
  /** Base colour; columns in the family get shades of it. */
  colors: string[];
}

// Per-sample detector internals with no shared prefix (mobile DETECTOR_DEBUG_COLUMNS).
const DETECTOR_DEBUG_COLUMNS = new Set([
  "Movement_Value",
  "Movement_Phase",
  "Movement_Direction",
  "Opposite_Motion_Met",
  "Secondary_Motion_Met",
  "Secondary_Movement_Value",
  "Template_Ready",
  "Template_Similarity",
  "Tilt_Delta_Deg",
  "Motion_Suppressed_Until_Release",
  "Artifact_Rejected",
]);

const FAMILIES: (Family & { test: (n: string) => boolean })[] = [
  { id: "axis", title: "Acceleration (X / Y / Z)", desc: "Sensor reading straight from the device, no baseline, no smoothing.",
    colors: ["#1d4ed8", "#3b82f6", "#93c5fd"], test: (n) => /^[XYZ]$/.test(n) },
  { id: "raw", title: "Raw", desc: "Unprocessed sensor reading, straight from the device.",
    colors: ["#0f766e", "#14b8a6", "#5eead4"], test: (n) => /^Raw_/.test(n) },
  { id: "gyro", title: "Gyroscope", desc: "Angular velocity from the gyroscope. Logged for analysis; not used by the rep detector.",
    colors: ["#7e22ce", "#a855f7", "#d8b4fe", "#6b21a8"], test: (n) => /^Gyro_/.test(n) },
  { id: "relative", title: "Relative", desc: "Raw minus the resting baseline captured during the countdown.",
    colors: ["#b45309", "#f59e0b", "#fcd34d"], test: (n) => /^Relative_/.test(n) },
  { id: "filteredRaw", title: "Filtered raw", desc: "Kalman-smoothed version of the raw signal.",
    colors: ["#4d7c0f", "#84cc16", "#bef264"], test: (n) => /^Filtered_Raw_/.test(n) },
  { id: "filteredRelative", title: "Filtered relative", desc: "Kalman-smoothed version of the baseline-relative signal.",
    colors: ["#be185d", "#ec4899", "#f9a8d4"], test: (n) => /^Filtered_/.test(n) },
  { id: "detector", title: "Detector — final detection", desc: "The exact signal the detector compares against the thresholds. The dashed threshold lines sit on these.",
    colors: ["#dc2626", "#ea580c", "#991b1b", "#f87171"], test: (n) => /^Detector_/.test(n) },
  { id: "detectorDebug", title: "Detector — debug internals", desc: "Per-sample detector internals logged when debug capture is on.",
    colors: ["#475569", "#64748b", "#94a3b8", "#334155"], test: (n) => DETECTOR_DEBUG_COLUMNS.has(n) },
];

const OTHER: Family = { id: "other", title: "Other", desc: "Other logged columns.", colors: ["#4b5563", "#9ca3af", "#1f2937"] };

export function familyFor(name: string): Family {
  return FAMILIES.find((f) => f.test(name)) || OTHER;
}

/** Columns grouped by family, in display order, empty families left out. */
export function groupSeries(names: string[]): { family: Family; names: string[] }[] {
  const order = [...FAMILIES.map((f) => f.id), OTHER.id];
  const buckets = new Map<string, string[]>();
  names.forEach((n) => {
    const id = familyFor(n).id;
    buckets.set(id, [...(buckets.get(id) || []), n]);
  });
  return order
    .filter((id) => buckets.has(id))
    .map((id) => ({ family: FAMILIES.find((f) => f.id === id) || OTHER, names: buckets.get(id)! }));
}

/** Colour for a column: a shade of its family's colour, by position in the family. */
export function colorFor(name: string, names: string[]): string {
  const fam = familyFor(name);
  const index = names.filter((n) => familyFor(n).id === fam.id).indexOf(name);
  return fam.colors[Math.max(index, 0) % fam.colors.length];
}

/**
 * Which columns start visible: saved choices first, otherwise only the Detector
 * family (the signal that decides whether a rep counted). Older files with no
 * Detector columns start with their first family on.
 */
export function initialVisibility(names: string[], saved: Record<string, boolean>): Record<string, boolean> {
  const groups = groupSeries(names);
  const defaultFamily = groups.some((g) => g.family.id === "detector") ? "detector" : groups[0]?.family.id;
  const vis: Record<string, boolean> = {};
  names.forEach((n) => {
    vis[n] = n in saved ? saved[n] : familyFor(n).id === defaultFamily;
  });
  return vis;
}

const STORAGE_KEY = "mrehabAdminGraphView";
export interface SavedView {
  series: Record<string, boolean>;
  thresholds: Record<string, boolean>;
}

/** The viewer's remembered choices; empty when storage is missing or corrupt. */
export function loadSaved(): SavedView {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    const pick = (v: unknown) => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, boolean>) : {});
    return { series: pick(raw?.series), thresholds: pick(raw?.thresholds) };
  } catch {
    return { series: {}, thresholds: {} };
  }
}

/** Merge choices into the remembered view (best effort; storage may be blocked). */
export function saveChoice(kind: keyof SavedView, choices: Record<string, boolean>): void {
  try {
    const current = loadSaved();
    current[kind] = { ...current[kind], ...choices };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch {
    /* storage unavailable: choices last for this page only */
  }
}
